// ---------------------------------------------------------------------------
// Tema MUI.
//
// Rolul ei e să facă componentele din bibliotecă să arate ca restul: butoane
// capsulă, câmpuri cu muchie de 1px, acordeoane fără umbră și fără linia
// aceea gri pe care MUI o desenează implicit deasupra fiecărui panou.
//
// Culorile vin din `tokens.js`. Unde MUI trebuie să calculeze singur o stare
// (hover, disabled, contrast), primește valoarea brută — `var(--qf-…)` nu poate
// fi descompus în canale. Unde nu calculează nimic, scriem direct variabila,
// ca să urmeze schema sistemului fără să dublăm regulile.
// ---------------------------------------------------------------------------

import { createTheme } from '@mui/material/styles';
import { FONT, TYPE, RADIUS, EASE_CSS, DUR, RAW } from './tokens';

const { light: L, dark: D } = RAW;

const button = {
  fontFamily: FONT.sans,
  fontWeight: 600,
  fontSize: '1.0625rem',
  letterSpacing: '-0.012em',
  textTransform: 'none',
  borderRadius: RADIUS.pill,
  // 48px = ținta de atingere confortabilă pe telefon (Apple recomandă 44 minim).
  minHeight: 48,
  paddingInline: 26,
  transition: `background-color ${DUR.fast}s ${EASE_CSS}, color ${DUR.fast}s ${EASE_CSS}, border-color ${DUR.fast}s ${EASE_CSS}, opacity ${DUR.fast}s ${EASE_CSS}`,
  '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 3 },
};

const theme = createTheme({
  // Schema se citește dintr-un atribut pe `<html>`, nu direct din
  // `prefers-color-scheme`. Diferența contează: preferința sistemului rămâne
  // valoarea implicită, dar comutatorul din bară o poate suprascrie, iar
  // alegerea se ține minte. Atributul e pus de scriptul din index.html înainte
  // de primul cadru, deci nu există pâlpâire albă la încărcare.
  cssVariables: { colorSchemeSelector: 'data' },
  defaultColorScheme: 'light',

  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: { main: L['--qf-accent'], contrastText: '#FFFFFF' },
        secondary: { main: RAW.amber, contrastText: '#1D1D1F' },
        error: { main: L['--qf-danger'] },
        success: { main: L['--qf-ok'] },
        background: { default: L['--qf-bg'], paper: L['--qf-card'] },
        text: { primary: L['--qf-ink'], secondary: '#6E6E73', disabled: L['--qf-ink-3'] },
        divider: L['--qf-line'],
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: { main: D['--qf-accent'], contrastText: D['--qf-accent-ink'] },
        secondary: { main: RAW.amberDark, contrastText: '#1D1D1F' },
        error: { main: D['--qf-danger'] },
        success: { main: D['--qf-ok'] },
        background: { default: D['--qf-bg'], paper: D['--qf-card'] },
        text: { primary: D['--qf-ink'], secondary: 'rgba(235,235,245,.68)', disabled: 'rgba(235,235,245,.4)' },
        divider: D['--qf-line'],
      },
    },
  },

  shape: { borderRadius: RADIUS.md },

  typography: {
    fontFamily: FONT.sans,
    h1: TYPE.h1,
    h2: TYPE.h2,
    h3: TYPE.h3,
    h4: { ...TYPE.h3, fontSize: '1.125rem' },
    body1: TYPE.body,
    body2: TYPE.small,
    caption: TYPE.caption,
    button: { textTransform: 'none', fontWeight: 600 },
  },

  components: {
    // MUI pune un gradient peste `Paper` în schema închisă, ca să sugereze
    // elevația. Aici adâncimea vine din treptele de gri din tokeni, iar
    // gradientul doar spală culoarea suprafeței.
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: button,
        sizeLarge: { minHeight: 54, paddingInline: 32, fontSize: '1.09375rem' },
        sizeSmall: { minHeight: 40, paddingInline: 18, fontSize: '0.9375rem' },
        contained: {
          backgroundColor: 'var(--qf-accent)',
          color: 'var(--qf-accent-ink)',
          '&:hover': { backgroundColor: 'var(--qf-accent-hover)' },
          '&.Mui-disabled': { backgroundColor: 'var(--qf-accent)', color: 'var(--qf-accent-ink)', opacity: 0.45 },
        },
        outlined: {
          borderColor: 'var(--qf-line-2)',
          color: 'var(--qf-ink)',
          '&:hover': { borderColor: 'var(--qf-ink-3)', backgroundColor: 'var(--qf-glass-tint)' },
        },
        text: {
          color: 'var(--qf-accent)',
          '&:hover': { backgroundColor: 'var(--qf-accent-soft)' },
        },
      },
      variants: [
        // Butonul de pe panourile închise: sticlă peste bleumarin, nu alb plin.
        {
          props: { variant: 'onDark' },
          style: {
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,.32)',
            backgroundColor: 'rgba(255,255,255,.06)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,.14)', borderColor: 'rgba(255,255,255,.5)' },
            '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: 3 },
          },
        },
        {
          props: { variant: 'onDarkFilled' },
          style: {
            backgroundColor: '#FFFFFF',
            color: '#0B2A5B',
            '&:hover': { backgroundColor: 'rgba(255,255,255,.88)' },
            '&:focus-visible': { outline: '2px solid #FFFFFF', outlineOffset: 3 },
          },
        },
      ],
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'var(--qf-ink)',
          transition: `background-color ${DUR.fast}s ${EASE_CSS}, color ${DUR.fast}s ${EASE_CSS}`,
          '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 2 },
        },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'none' },
      styleOverrides: {
        root: {
          color: 'var(--qf-accent)',
          transition: `opacity ${DUR.fast}s ${EASE_CSS}`,
          '&:hover': { opacity: 0.72 },
          '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 2, borderRadius: 4 },
        },
      },
    },

    // Acordeonul MUI vine cu umbră, cu margini care sar la deschidere și cu o
    // linie `::before` între rânduri. Aici e doar un rând dintr-o listă.
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0, square: true },
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
          borderTop: '1px solid var(--qf-line)',
          '&:first-of-type': { borderTop: 'none' },
          '&::before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 60,
          paddingInline: 0,
          gap: 16,
          '&.Mui-expanded': { minHeight: 60 },
          '&:focus-visible': {
            outline: '2px solid var(--qf-accent)',
            outlineOffset: -2,
            borderRadius: RADIUS.sm,
            backgroundColor: 'transparent',
          },
        },
        content: { marginBlock: 14, '&.Mui-expanded': { marginBlock: 14 } },
        expandIconWrapper: {
          color: 'var(--qf-ink-3)',
          transition: `transform ${DUR.base}s ${EASE_CSS}, color ${DUR.base}s ${EASE_CSS}`,
          '&.Mui-expanded': { color: 'var(--qf-accent)' },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: { root: { padding: '0 0 22px', maxWidth: '70ch' } },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS.md,
          backgroundColor: 'var(--qf-card-2)',
          fontSize: '1.0625rem',
          transition: `border-color ${DUR.fast}s ${EASE_CSS}`,
          '& fieldset': { borderColor: 'var(--qf-line)' },
          '&:hover fieldset': { borderColor: 'var(--qf-line-2)' },
          '&.Mui-focused fieldset': { borderColor: 'var(--qf-accent)', borderWidth: 2 },
          '&.Mui-error fieldset': { borderColor: 'var(--qf-danger)' },
        },
        // 16px minim pe câmpuri: sub această valoare, Safari pe iPhone
        // mărește singur pagina când câmpul primește focus.
        input: { fontSize: '1.0625rem', paddingBlock: 15 },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'var(--qf-ink-3)',
          '&.Mui-focused': { color: 'var(--qf-accent)' },
          '&.Mui-error': { color: 'var(--qf-danger)' },
        },
        // Steluța de „obligatoriu". Implicit o moștenește pe culoarea
        // etichetei, adică gri pe gri: singurul semn care spune ce nu poate
        // rămâne gol se pierdea în text. Pe accent se vede dintr-o privire, iar
        // roșul îl păstrăm pentru ce chiar e greșit.
        asterisk: {
          color: 'var(--qf-accent)',
          '&.Mui-error': { color: 'var(--qf-danger)' },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginInline: 2,
          fontSize: '0.8125rem',
          color: 'var(--qf-ink-3)',
          '&.Mui-error': { color: 'var(--qf-danger)' },
        },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: 'var(--qf-line)' } } },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: 13, borderRadius: RADIUS.sm, paddingInline: 10, paddingBlock: 7 },
      },
    },
  },
});

export default theme;
